import DynamicDialog from '@/components/DynamicDialog';
import UserDialog from '@/components/UserDialog';
import { userAtom } from '@/global-store';
import useLogoutFn from '@/hooks/useLogoutFn';
import ChangePasswordForm from '@/pages/authentication/auth-forms/ChangePasswordForm';
import { Pencil, KeyRound, LogOut, User, Copy, Check } from 'lucide-react';
import { useAtom } from 'jotai';
import { Fragment, useState } from 'react';
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Typography,
} from '@eye-able/ui';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';

function ProfileSection() {
  const { t, ts } = useInlineTranslation();
  const [user] = useAtom(userAtom);
  const logoutFn = useLogoutFn();

  const [copied, setCopied] = useState(false);

  const handleCopyApiKey = async () => {
    if (!user?.apiKey) return;
    await navigator.clipboard.writeText(user.apiKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const userInfo = {
    name: { label: t('name'), value: user?.firstName + ' ' + user?.lastName },
    email: { label: t('email'), value: user?.email },
    phone: { label: t('phone'), value: user?.phone },
    company: { label: t('company'), value: user?.company },
  };

  return (
    <Popover modal>
      <PopoverTrigger asChild>
        <Button type='button' variant='ghost'>
          <User aria-hidden />
          <span className='eye:max-lg:sr-only'>{t('profile')}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        // lower z-index to be below other modals from this popover
        className='eye:z-1300 eye:p-s! eye:pt-m eye:max-w-[400px] eye:max-h-(--radix-popover-content-available-height)'
        align={'end'}
        matchTriggerWidth={false}
        collisionPadding={16}
      >
        <Typography tag='h4'>{t('profileHeader')}</Typography>
        <div className='eye:mt-2xs eye:mb-s eye:flex eye:flex-col eye:gap-3xs'>
          {Object.entries(userInfo).map(([key, { label, value }]) => (
            <div key={key}>
              <Typography variant='body-s-highlight'>{label}</Typography>
              <Typography className='eye:break-all'>
                {value || ' - '}
              </Typography>
            </div>
          ))}
        </div>
        <div className='eye:mb-s'>
          <Typography variant='body-s-highlight'>{t('key')}</Typography>
          <div className='eye:flex eye:items-center eye:gap-2xs'>
            <Typography className='eye:break-all eye:flex-1'>
              {'•'.repeat(24)}
            </Typography>
            <button
              type='button'
              onClick={() => void handleCopyApiKey()}
              aria-label={ts('copy')}
              disabled={!navigator.clipboard}
              className='eye:shrink-0 eye:cursor-pointer eye:rounded eye:p-3xs eye:text-current eye:transition-colors eye:hover:bg-black/8 eye:disabled:cursor-not-allowed eye:disabled:opacity-40'
            >
              {copied ? (
                <Check size={16} aria-hidden />
              ) : (
                <Copy size={16} aria-hidden />
              )}
            </button>
          </div>
        </div>
        <div className='eye:flex eye:flex-col eye:gap-2xs'>
          <UserDialog
            hideOrgAdminToggle
            title={t('editProfile')}
            userData={user}
            useUiButton
            activatorProps={{
              className: 'eye:w-full',
              variant: 'outline',
            }}
            activatorText={
              <Fragment>
                <Pencil aria-hidden />
                {t('editProfile')}
              </Fragment>
            }
            maxWidth='md'
          />

          <DynamicDialog
            title={t('changePassword')}
            activatorText={
              <Fragment>
                <KeyRound aria-hidden />
                {t('changePassword')}
              </Fragment>
            }
            useUiButton
            activatorProps={{
              variant: 'outline',
              className: 'eye:w-full',
            }}
            hideActions
          >
            {({ handleCancel }: { handleCancel: () => void }) => (
              <div className='eye:p-3xs'>
                <ChangePasswordForm onCancel={handleCancel} />
              </div>
            )}
          </DynamicDialog>

          <Button
            type='button'
            onClick={() => void logoutFn()}
            variant='outline'
          >
            <LogOut aria-hidden />
            {t('logOutSB')}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default ProfileSection;
